import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useGetAllClasses } from '~entities/classes/index';
import { useGetAllEvents } from '~entities/events';
import { useGetPolicy, useUpdatePolicyMutation } from '~entities/policies';
import { useGetAllSessions } from '~entities/session/api/get-sessions';

import MultipleSelector, {
  type Option,
} from '~shared/shadcn/expansions/multiple-selector';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

interface FormData {
  name: string;
  description: string;
  classes: Array<{
    label: string;
    value: string;
  }>;
  events: Array<{
    label: string;
    value: string;
  }>;
  sessions: Array<{
    label: string;
    value: string;
  }>;
}

export const UpdatePolicyPage = () => {
  const { policyId } = useParams({
    from: '/_auth/_layout/policies/$policyId/',
  });
  const navigate = useNavigate({ from: '/policies/$policyId' });
  const mutation = useUpdatePolicyMutation();
  const [classOptions, setClassOptions] = useState<Array<Option>>([]);
  const [eventOptions, setEventOptions] = useState<Array<Option>>([]);
  const { data: classesData } = useGetAllClasses();
  const { data: eventsData } = useGetAllEvents();
  const { data: sessionsData } = useGetAllSessions({
    page: 1,
    limit: 1000,
    isArchive: false,
  });
  const { data: policyData } = useGetPolicy({ policyId });

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      classes: [],
      events: [],
      sessions: [],
    },
    values: policyData
      ? {
          name: policyData.name,
          description: policyData.description,
          classes: policyData.class.map(
            (classItem: { title: string; _id: string }) => ({
              label: classItem.title,
              value: classItem._id,
            }),
          ),
          events: policyData.event.map(
            (eventItem: { title: string; _id: string }) => ({
              label: eventItem.title,
              value: eventItem._id,
            }),
          ),
          sessions: [],
        }
      : {
          name: '',
          description: '',
          classes: [],
          events: [],
          sessions: [],
        },
  });
  useEffect(() => {
    if (classesData) {
      const mappedOptions = classesData.map((classItem) => ({
        label: classItem.title,
        value: classItem._id,
      }));
      setClassOptions(mappedOptions);
    }
  }, [classesData]);

  useEffect(() => {
    if (eventsData) {
      const mappedOptions = eventsData.map((eventItem) => ({
        label: eventItem.title,
        value: eventItem._id,
      }));
      setEventOptions(mappedOptions);
    }
  }, [eventsData]);

  const sessionOptions = useMemo(() => {
    return (
      sessionsData?.sessions.map((session) => ({
        label: session.name,
        value: session._id,
      })) ?? []
    );
  }, [sessionsData]);

  const handleSessionsChange = useCallback(
    (newSessions: Array<Option>) => {
      if (!classesData || !eventsData) {
        return;
      }

      const currentClasses = getValues('classes') ?? [];
      const currentEvents = getValues('events') ?? [];
      const currentClassIds = new Set(currentClasses.map((c) => c.value));
      const currentEventIds = new Set(currentEvents.map((e) => e.value));
      const selectedSessionIds = new Set(newSessions.map((s) => s.value));

      const classesToAdd: Array<Option> = [];
      for (const classItem of classesData) {
        if (
          classItem.session?._id &&
          selectedSessionIds.has(classItem.session._id) &&
          !currentClassIds.has(classItem._id)
        ) {
          classesToAdd.push({ label: classItem.title, value: classItem._id });
        }
      }

      const eventsToAdd: Array<Option> = [];
      for (const eventItem of eventsData) {
        if (
          eventItem.session?._id &&
          selectedSessionIds.has(eventItem.session._id) &&
          !currentEventIds.has(eventItem._id)
        ) {
          eventsToAdd.push({ label: eventItem.title, value: eventItem._id });
        }
      }

      if (classesToAdd.length > 0) {
        setValue('classes', [...currentClasses, ...classesToAdd], {
          shouldValidate: false,
        });
      }
      if (eventsToAdd.length > 0) {
        setValue('events', [...currentEvents, ...eventsToAdd], {
          shouldValidate: false,
        });
      }
    },
    [classesData, eventsData, getValues, setValue],
  );

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      await mutation.mutateAsync({
        policyId,
        policyDetails: {
          name: data.name,
          description: data.description,
          class: data.classes.map((c) => c.value),
          event: data.events.map((e) => e.value),
        },
      });
      toast.success('Policy updated successfully!');
      void navigate({ to: '/policies' });
    } catch (error) {
      console.error('Failed to update policy:', error);
      toast.error('Failed to update policy. Please try again.');
    }
  };

  return (
    <div className='pb-56'>
      <Link to='/policies' className='flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Edit Policy</h2>
      </Link>
      <Card className='mx-auto mt-6 w-full max-w-4xl border border-border pt-4 shadow-none'>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label>Policy Name</Label>
                <Input
                  {...register('name', {
                    required: 'Policy name is required',
                  })}
                  placeholder='Policy Name'
                />
                {errors.name && (
                  <p className='text-red-500'>{errors.name.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label>Description</Label>
                <textarea
                  {...register('description', {
                    required: 'Description is required',
                    minLength: {
                      value: 10,
                      message: 'Description must be at least 10 characters',
                    },
                  })}
                  placeholder='Enter Policy description'
                  className='h-96 w-full rounded-md border p-2'
                />
                {errors.description && (
                  <p className='text-red-500'>{errors.description.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label>Sessions</Label>
                {sessionOptions.length > 0 && (
                  <Controller
                    control={control}
                    name='sessions'
                    render={({ field }) => (
                      <MultipleSelector
                        options={sessionOptions}
                        value={field.value}
                        onChange={(options) => {
                          handleSessionsChange(options as Array<Option>);
                          field.onChange(options);
                        }}
                        placeholder='Select sessions to apply policy to all classes/events...'
                        emptyIndicator={
                          <p className='text-center text-lg leading-10 text-gray-600 dark:text-gray-400'>
                            No sessions found.
                          </p>
                        }
                      />
                    )}
                  />
                )}
              </div>
              <div className='space-y-2'>
                <Label>Classes</Label>
                {classOptions.length > 0 && (
                  <Controller
                    control={control}
                    name='classes'
                    render={({ field }) => (
                      <MultipleSelector
                        options={classOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder='Select classes...'
                        emptyIndicator={
                          <p className='text-center text-lg leading-10 text-gray-600 dark:text-gray-400'>
                            No classes found.
                          </p>
                        }
                      />
                    )}
                  />
                )}
              </div>
              <div className='space-y-2'>
                <Label>Events</Label>
                {eventOptions.length > 0 && (
                  <Controller
                    control={control}
                    name='events'
                    render={({ field }) => (
                      <MultipleSelector
                        options={eventOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder='Select events...'
                        emptyIndicator={
                          <p className='text-center text-lg leading-10 text-gray-600 dark:text-gray-400'>
                            No events found.
                          </p>
                        }
                      />
                    )}
                  />
                )}
              </div>
            </div>

            <Button type='submit'>
              {mutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Updating Policy
                </>
              ) : (
                'Update Policy'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
